/**
 * Monitors page visibility changes that can interrupt audio recording.
 *
 * On iOS Safari, backgrounding the page (screen lock, app switch, tab switch)
 * kills active MediaStream tracks and suspends AudioContext. This monitor
 * detects those transitions and reports them so the recording state machine
 * can warn the user or attempt recovery.
 */
export interface PageVisibilityMonitorConfig {
  /** Called when the page becomes hidden (user backgrounded) */
  onHidden: () => void;
  /** Called when the page becomes visible again, with ms spent hidden */
  onVisible: (hiddenDurationMs: number) => void;
  /**
   * Optional: called on return from background if audio tracks are no longer
   * live. This indicates iOS killed the recording while backgrounded.
   */
  onRecordingInterrupted?: () => void;
  /** Supply the active MediaStream so we can check track health on return */
  getMediaStream?: () => MediaStream | null;
  /** Logging function */
  logger?: (
    level: 'info' | 'warn',
    message: string,
    meta?: Record<string, unknown>,
  ) => void;
}

export class PageVisibilityMonitor {
  private hiddenAt: number | null = null;
  private config: PageVisibilityMonitorConfig;
  private boundHandler: () => void;

  constructor(config: PageVisibilityMonitorConfig) {
    this.config = config;
    this.boundHandler = this.handleVisibilityChange.bind(this);
  }

  start(): void {
    document.addEventListener('visibilitychange', this.boundHandler);
    this.config.logger?.('info', 'PageVisibilityMonitor started');
  }

  stop(): void {
    document.removeEventListener('visibilitychange', this.boundHandler);
    this.hiddenAt = null;
    this.config.logger?.('info', 'PageVisibilityMonitor stopped');
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now();
      this.config.onHidden();
      this.config.logger?.('warn', 'Page became hidden during recording');
    } else if (document.visibilityState === 'visible') {
      const hiddenDurationMs = this.hiddenAt ? Date.now() - this.hiddenAt : 0;
      this.hiddenAt = null;

      this.config.logger?.('info', 'Page became visible after backgrounding', {
        hiddenDurationMs,
      });

      this.config.onVisible(hiddenDurationMs);

      // Check if audio tracks survived the backgrounding
      if (this.config.getMediaStream && this.config.onRecordingInterrupted) {
        const stream = this.config.getMediaStream();
        if (stream) {
          const hasLiveTracks = stream
            .getAudioTracks()
            .some((track) => track.readyState === 'live' && track.enabled);

          if (!hasLiveTracks) {
            this.config.logger?.(
              'warn',
              'Audio tracks died while page was hidden',
              {
                hiddenDurationMs,
                tracks: stream.getAudioTracks().map((t) => ({
                  readyState: t.readyState,
                  enabled: t.enabled,
                  label: t.label,
                })),
              },
            );
            this.config.onRecordingInterrupted();
          }
        }
      }
    }
  }
}
